import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ScannerMode from './components/ScannerMode';
import TabletMode from './components/TabletMode';
import ManualMode from './components/ManualMode';
import BadgePreview from './components/BadgePreview';
import ConfirmDialog from './components/ConfirmDialog';
import LabelPreview from './components/LabelPreview';
import DfpLogo from './components/DfpLogo';
import { api } from './services/api';
import { storage } from './services/storage';

function App() {
    const [mode, setMode] = useState('scanner'); // 'scanner', 'tablet', 'manual'
    const [selectedEvent, setSelectedEvent] = useState('');
    const [currentRegistration, setCurrentRegistration] = useState(null);
    const [message, setMessage] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingRegistration, setPendingRegistration] = useState(null);
    const [showEventSelector, setShowEventSelector] = useState(false);
    const [pendingRegistrations, setPendingRegistrations] = useState([]);
    const [lastManualSearch, setLastManualSearch] = useState({ term: '', results: [] });
    const [showLabelPreview, setShowLabelPreview] = useState(false);

    // Load events on mount
    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            const eventList = await api.getEvents();
            
            // Auto-select first event if available
            if (eventList.length > 0) {
                setSelectedEvent(eventList[0].id);
            }
        } catch (error) {
            console.error('Failed to load events:', error);
            setMessage({ type: 'error', text: 'Failed to load events: ' + error.message });
        }
    };

    const handleScanResult = async (qrCode) => {
        setMessage({ type: 'info', text: 'Looking up registration...' });

        try {
            const result = await api.searchByQR(qrCode);
            
            // Check if multiple registrations returned
            if (result.multiple && result.registrations && result.registrations.length > 1) {
                // Show selection dialog
                setPendingRegistrations(result.registrations);
                setShowEventSelector(true);
                setMessage(null);
            } else if (Array.isArray(result)) {
                // Array of registrations
                if (result.length > 1) {
                    setPendingRegistrations(result);
                    setShowEventSelector(true);
                    setMessage(null);
                } else if (result.length === 1) {
                    handleRegistrationFound(result[0]);
                } else {
                    setMessage({ type: 'error', text: 'No registrations found' });
                }
            } else {
                // Single registration object
                handleRegistrationFound(result);
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message || 'Registration not found' });
        }
    };

    const handleRegistrationFound = (registration) => {
        setMessage(null);
        
        // Check if already printed today
        const printed = storage.wasPrintedToday(registration.qrCode);
        
        if (printed) {
            setPendingRegistration(registration);
            setShowConfirm(true);
        } else {
            setCurrentRegistration(registration);
        }
    };

    const handleConfirmPrint = () => {
        setShowConfirm(false);
        setCurrentRegistration(pendingRegistration);
        setPendingRegistration(null);
    };

    const handleCancelPrint = () => {
        setShowConfirm(false);
        setPendingRegistration(null);
        setMessage({ type: 'info', text: 'Print cancelled' });
    };

    const handlePrintComplete = (registration) => {
        const count = storage.markAsPrinted(registration.qrCode, registration.name);
        setMessage({ 
            type: 'success', 
            text: `✅ Badge printed successfully for ${registration.name}` + (count > 1 ? ` (${count} times today)` : '')
        });
        setCurrentRegistration(null);
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(null), 3000);
    };

    const handleCancel = () => {
        setCurrentRegistration(null);
        setMessage(null);
        // Manual mode will restore search when remounted
    };

    const handleSaveSearchState = useCallback((searchTerm, results) => {
        setLastManualSearch({ term: searchTerm, results });
    }, []);

    const handleSelectRegistrationFromList = (registration) => {
        handleRegistrationFound(registration);
        setShowEventSelector(false);
        setPendingRegistrations([]);
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setCurrentRegistration(null);
        setMessage(null);
    };

    return (
        <div className="App">
            <header className="header">
                <div className="header-content">
                    <div className="header-brand">
                        <DfpLogo className="header-logo" />
                        <span className="header-divider" aria-hidden="true"></span>
                        <h1>Badge Scanner</h1>
                    </div>
                    <button 
                        className="label-preview-btn"
                        onClick={() => setShowLabelPreview(!showLabelPreview)}
                        title="Toggle Label Preview (Debug)"
                        style={{ display: 'none' }}
                    >
                        📐
                    </button>
                </div>
            </header>

            <main className="main-content">
                {!currentRegistration && (
                    <>
                        <div className="mode-selector">
                            <button 
                                className={`mode-btn ${mode === 'scanner' ? 'active' : ''}`}
                                onClick={() => handleModeChange('scanner')}
                            >
                                Scanner
                            </button>
                            <button 
                                className={`mode-btn ${mode === 'tablet' ? 'active' : ''}`}
                                onClick={() => handleModeChange('tablet')}
                                style={{ display: 'none' }}
                            >
                                Tablet
                            </button>
                            <button 
                                className={`mode-btn ${mode === 'manual' ? 'active' : ''}`}
                                onClick={() => handleModeChange('manual')}
                            >
                                Manual
                            </button>
                        </div>

                        {message && (
                            <div className={`message ${message.type}`}>
                                {message.text}
                            </div>
                        )}

                        <div className="scanner-container">
                            {mode === 'scanner' && (
                                <ScannerMode 
                                    onScanResult={handleScanResult}
                                    eventId={selectedEvent}
                                />
                            )}
                            {mode === 'tablet' && (
                                <TabletMode 
                                    onScanResult={handleScanResult}
                                    eventId={selectedEvent}
                                />
                            )}
                            {mode === 'manual' && (
                                <ManualMode 
                                    onSelectRegistration={handleRegistrationFound}
                                    eventId={selectedEvent}
                                    onSaveSearchState={handleSaveSearchState}
                                    initialSearchState={lastManualSearch}
                                />
                            )}
                        </div>
                    </>
                )}

                {currentRegistration && (
                    <BadgePreview 
                        registration={currentRegistration}
                        onPrintComplete={handlePrintComplete}
                        onCancel={handleCancel}
                    />
                )}
            </main>

            {showConfirm && pendingRegistration && (
                <ConfirmDialog 
                    message={`Badge for ${pendingRegistration.name} has already been printed ${storage.wasPrintedToday(pendingRegistration.qrCode).count} time(s) today. Print again?`}
                    onConfirm={handleConfirmPrint}
                    onCancel={handleCancelPrint}
                />
            )}

            {showEventSelector && pendingRegistrations.length > 1 && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Multiple Registrations Found</h3>
                        <p>This contact is registered for multiple events. Please select which event to print the badge for:</p>
                        <div className="results-list">
                            {pendingRegistrations.map((reg, index) => (
                                <div key={reg.qrCode || reg.id || index} className="result-item">
                                    <div className="result-info">
                                        <h3>{reg.name}</h3>
                                        <p>{reg.eventName || 'Event'}</p>
                                        <p style={{ fontSize: '0.8rem', color: '#999' }}>
                                            {reg.title} at {reg.company}
                                        </p>
                                    </div>
                                    <button 
                                        className="select-btn"
                                        onClick={() => handleSelectRegistrationFromList(reg)}
                                    >
                                        Select
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                            <button 
                                className="modal-btn secondary"
                                onClick={() => {
                                    setShowEventSelector(false);
                                    setPendingRegistrations([]);
                                    setMessage(null);
                                }}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLabelPreview && (
                <div className="label-preview-modal">
                    <div className="label-preview-modal-content">
                        <button 
                            className="modal-close-btn"
                            onClick={() => setShowLabelPreview(false)}
                        >
                            ✕
                        </button>
                        <LabelPreview />
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
